<template>
  <div class="reply-modal-wrapper" v-if="show">
    <div class="reply-modal-backdrop" @click="closeModal"></div>
    <div class="reply-modal">
      <div class="reply-modal-header">
        <h3 class="typo-headline-6">{{ isQuote ? 'Responder con cita' : 'Responder al hilo' }}</h3>
        <button class="close-modal-btn" @click="closeModal">
          <i class="material-icons">close</i>
        </button>
      </div>
      
      <!-- Quote section (if replying to a specific comment) -->
      <div v-if="isQuote && quotedComment" class="quoted-content">
        <div class="quote-header">
          <span class="quote-author">{{ quotedComment.name }}</span>
          <span class="quote-number">#{{ quotedCommentNumber }}</span>
        </div>
        <div class="quote-body typo-body-2 comment-body-content" v-html="quotedComment.comment_body_value"></div>
      </div>
      
      <!-- Reply form -->
      <div class="reply-form-container">
        <form @submit.prevent="submitReply">
          <div class="form-group">
            <label for="reply-content" class="typo-subtitle-1">Tu respuesta</label>
            <!-- Replace textarea with WYSIWYG editor -->
            <wysiwyg-editor
              v-model="replyContent"
              name="reply-content"
              placeholder="Escribe tu respuesta..."
              ref="wysiwygEditor"
            />
          </div>
          
          <div class="form-actions">
            <button type="button" class="cancel-btn typo-button" @click="closeModal">
              Cancelar
            </button>
            <button type="submit" class="submit-btn typo-button" :disabled="submitting">
              <span v-if="submitting">Enviando...</span>
              <span v-else>Publicar respuesta</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import forumApi from '@/services/forumApi';
const axios = forumApi;
import WysiwygEditor from '@/components/WysiwygEditor.vue';

export default {
  name: 'ReplyModal',
  components: {
    WysiwygEditor
  },
  props: {
    show: {
      type: Boolean,
      required: true
    },
    threadId: {
      type: [String, Number],
      required: true
    },
    quotedComment: {
      type: Object,
      default: null
    },
    quotedCommentNumber: {
      type: Number,
      default: null
    }
  },
  data() {
    return {
      replyContent: '',
      submitting: false,
      error: null
    };
  },
  computed: {
    isQuote() {
      return this.quotedComment !== null;
    }
  },
  watch: {
    show(newVal) {
      if (newVal) {
        // When modal is shown, focus the editor and prepare content if quoting
        this.$nextTick(() => {
          // If we're quoting a comment, prepare the quoted text
          if (this.isQuote && this.quotedComment) {
            // We don't auto-fill the quote in the textarea as we're showing it separately
            this.replyContent = '';
          }
        });
      } else {
        // Reset form when modal is closed
        this.replyContent = '';
        this.submitting = false;
        this.error = null;
      }
    }
  },
  methods: {
    closeModal() {
      this.$emit('close');
    },
    submitReply() {
      if (!this.replyContent.trim()) {
        return;
      }
      
      this.submitting = true;
      
      // Prepare the data for the API
      const replyData = {
        nid: this.threadId,
        comment_body: {
          value: this.replyContent
        }
      };
      
      // If we're replying to a specific comment, include the parent ID
      if (this.isQuote && this.quotedComment && this.quotedComment.cid) {
        replyData.pid = this.quotedComment.cid;
      }
      
      // Send the request to the API
      axios.post('/forum/api/comment/create', replyData)
        .then(response => {
          if (response.data && response.data.success) {
            // Emit an event to notify the parent component that the reply was submitted
            this.$emit('reply-submitted', response.data);
            
            // Close the modal
            this.closeModal();
          } else {
            // Handle error from API
            this.error = response.data.error || 'Error al enviar la respuesta';
          }
        })
        .catch(err => {
          console.error('Error submitting reply:', err);
          this.error = 'Error al enviar la respuesta. Por favor, inténtalo de nuevo.';
        })
        .finally(() => {
          this.submitting = false;
        });
    }
  }
};
</script>

<style scoped>
.reply-modal-wrapper {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.reply-modal-backdrop {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
}

.reply-modal {
  position: relative;
  width: 90%;
  max-width: 800px;
  max-height: 90vh;
  background-color: var(--color-surface);
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  overflow-y: auto;
  z-index: 1001;
  display: flex;
  flex-direction: column;
}

.reply-modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid var(--color-outline);
}

.reply-modal-header h3 {
  margin: 0;
  color: var(--color-text-primary);
}

.close-modal-btn {
  background: none;
  border: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  padding: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: background-color 0.3s ease;
}

.close-modal-btn:hover {
  background-color: var(--color-active-surface);
}

.quoted-content {
  margin: 20px 20px 0 20px;
  padding: 15px;
  background-color: var(--color-active-surface);
  border-left: 3px solid var(--color-highlight);
  border-radius: 4px;
}

.quote-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  color: var(--color-text-primary);
}

.quote-author {
  font-weight: 500;
  color: var(--color-text-primary);
}

.quote-number {
  color: var(--color-text-secondary);
}

.quote-body {
  color: var(--color-text-primary);
  white-space: pre-line;
  overflow: hidden;
  text-overflow: ellipsis;
  max-height: 150px;
}

/* Base blockquote styling for quoted content in ReplyModal */
.quoted-content .comment-body-content ::v-deep blockquote {
  background-color: var(--color-background, rgba(255, 255, 255, 0.5));
  border-left: 3px solid var(--color-text-secondary, #666);
  margin: 0.5em 0;
  padding: 0.5em 0.8em;
  font-size: 0.9em;
  border-radius: 3px;
  opacity: 0.8;
  color: var(--color-text-primary);
}

/* Deeply nested blockquotes in ReplyModal */
.quoted-content .comment-body-content ::v-deep blockquote blockquote {
  background-color: var(--color-outline, rgba(0, 0, 0, 0.1));
  border-left: 2px solid var(--color-text-secondary, #999);
  margin: 0.3em 0;
  padding: 0.3em 0.6em;
  font-size: 0.85em;
  opacity: 0.7;
}

.reply-form-container {
  padding: 20px;
}

.form-group {
  margin-bottom: 20px;
  width: 100%;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--color-text-primary);
}

.form-control {
  width: 100%;
  max-width: 100%;
  padding: 12px;
  border: 1px solid var(--color-outline);
  border-radius: 4px;
  background-color: var(--color-background);
  color: var(--color-text-primary);
  font-family: inherit;
  transition: border-color 0.3s ease;
  box-sizing: border-box;
}

textarea.form-control {
  resize: vertical;
}

.form-control:focus {
  border-color: var(--color-highlight);
  outline: none;
}

.form-control::placeholder {
  color: var(--color-text-secondary);
  opacity: 0.7;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 15px;
  margin-top: 20px;
}

.cancel-btn, .submit-btn {
  padding: 10px 20px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.cancel-btn {
  background-color: var(--color-background);
  color: var(--color-text-primary);
}

.cancel-btn:hover {
  background-color: var(--color-outline);
}

.submit-btn {
  background-color: var(--color-highlight);
  color: var(--color-on-highlight, white);
}

.submit-btn:hover:not(:disabled) {
  background-color: var(--color-highlight-dark, #1976d2);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .reply-modal {
    width: 95%;
    max-height: 95vh;
  }
  
  .form-actions {
    flex-direction: column;
  }
  
  .cancel-btn, .submit-btn {
    width: 100%;
  }
}

/* Remove the textarea styles since we're using the WYSIWYG editor */
textarea.form-control {
  display: none;
}
</style> 